import { z } from "zod";
import {
  paginationMetaSchema,
  tenantBusinessMembershipListSchema,
  tenantCategoryListSchema,
  tenantCategorySchema,
  tenantMenuItemListSchema,
  tenantMenuItemSchema,
  tenantMenuItemImageSchema,
  tenantCurrentBusinessSchema,
  tenantLoginResponseSchema,
  tenantMeResponseSchema,
  tenantRegisterResponseSchema,
  tenantTeamMemberSchema,
  tenantTeamMemberListSchema,
  tenantTeamInvitationSchema,
  tenantTeamInvitationListSchema,
  tenantInvitationInspectSchema,
  tenantMenuImportPreviewResponseSchema,
  tenantMenuImportConfirmResponseSchema,
  type CreateTenantCategoryInput,
  type CreateTenantMenuItemInput,
  type UpdateTenantCategoryInput,
  type UpdateTenantMenuItemInput,
  type UpsertTenantMenuItemImageInput,
  type ErrorEnvelope,
  type PaginationMeta,
  type SuccessEnvelope,
  type TenantBusinessMembership,
  type TenantCurrentBusiness,
  type PublishTenantBusinessInput,
  type UpdateTenantBusinessBrandingInput,
  type UpdateTenantBusinessProfileInput,
  type TenantCategory,
  type TenantMenuItem,
  type TenantMenuItemImage,
  type TenantLoginResponse,
  type TenantMeResponse,
  type TenantRegisterInput,
  type TenantRegisterResponse,
  type ChangeTenantPasswordInput,
  type ForgotPasswordInput,
  type ResetPasswordInput,
  type VerifyTenantEmailInput,
  type TenantTeamMember,
  type TenantTeamInvitation,
  type UpdateTenantTeamMemberRoleInput,
  type CreateTenantInvitationInput,
  type TenantInvitationInspect,
  type InspectTenantInvitationInput,
  type AcceptTenantInvitationInput,
  type TenantMenuImportPreviewResponse,
  type TenantMenuImportConfirmResponse,
  type PreviewTenantMenuImportCSVInput,
  type PreviewTenantMenuImportURLInput,
  type PreviewTenantMenuImportPDFInput,
  type ConfirmTenantMenuImportInput,
} from "./types";

export interface TenantLoginCredentials {
  email: string;
  password: string;
}

export interface TenantCategoryListParams {
  includeInactive?: boolean;
  limit?: number;
  offset?: number;
}

export interface TenantCategoryListResult {
  items: TenantCategory[];
  meta: PaginationMeta;
}

export interface TenantMenuItemListParams {
  categoryId?: number;
  isActive?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface TenantMenuItemListResult {
  items: TenantMenuItem[];
  meta: PaginationMeta;
}

export class TenantApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "TenantApiError";
    this.status = status;
    this.code = code;
  }
}

export interface TenantClient {
  login(credentials: TenantLoginCredentials): Promise<TenantLoginResponse>;
  register(input: TenantRegisterInput): Promise<TenantRegisterResponse>;
  me(): Promise<TenantMeResponse>;
  changePassword(input: ChangeTenantPasswordInput): Promise<void>;
  /**
   * Unauthenticated. Always resolves on a 202 — the backend answers the same
   * fixed, enumeration-safe response whether or not the email belongs to an
   * account. Rejects with TenantApiError (status 429/503/4xx/5xx) or a plain
   * fetch error on a network failure; the caller distinguishes those to pick
   * an operational message, never an account-existence one.
   */
  forgotPassword(input: ForgotPasswordInput): Promise<void>;
  /**
   * Unauthenticated. Resolves on 204 No Content — no session token is issued
   * or stored. Rejects with TenantApiError(422) for any unusable token
   * (unknown, expired, already used, or an inactive account) — the backend
   * deliberately never distinguishes those reasons.
   */
  resetPassword(input: ResetPasswordInput): Promise<void>;
  /**
   * Unauthenticated. Never attaches a bearer token, even if the current tab
   * happens to hold one in memory — mirroring forgotPassword/resetPassword's
   * own public-request path, not authorizedNoContentRequest. Resolves on 204
   * No Content. Rejects with TenantApiError(422) for any unusable token
   * (unknown, expired, already used, or an inactive account) — the backend
   * deliberately never distinguishes those reasons.
   */
  verifyEmail(input: VerifyTenantEmailInput): Promise<void>;
  /**
   * Authenticated. No request body — no email, user id, or business id: the
   * target is the caller's own verified session. Resolves on 204 No Content,
   * including the already-verified no-op case. Rejects with
   * TenantApiError(503) when mail delivery is disabled for this deployment.
   */
  resendEmailVerification(): Promise<void>;
  businesses(): Promise<TenantBusinessMembership[]>;
  switchBusiness(businessId: number): Promise<TenantLoginResponse>;
  currentBusiness(): Promise<TenantCurrentBusiness>;
  setBusinessPublished(
    input: PublishTenantBusinessInput,
  ): Promise<TenantCurrentBusiness>;
  /**
   * Owner/admin only (backend-enforced; this client performs no role check
   * of its own). Full-replacement PUT of all four branding presets in one
   * call — no partial update. Resolves on 204 No Content, mirroring
   * deleteCategory/removeTeamMember's no-content pattern: the caller is
   * expected to already hold the values it just sent, not re-fetch them.
   */
  updateBusinessBranding(
    input: UpdateTenantBusinessBrandingInput,
  ): Promise<void>;
  /**
   * Owner/admin only (backend-enforced; this client performs no role check
   * of its own). Narrow write of only name/address/phone/logo_url/
   * location_url — never slug, theme_id, is_published or branding. Resolves
   * with the backend's
   * server-normalized CurrentBusinessResponse (trimmed values), so the
   * caller can update its local form state directly from the result instead
   * of issuing a follow-up currentBusiness() call.
   */
  updateBusinessProfile(
    input: UpdateTenantBusinessProfileInput,
  ): Promise<TenantCurrentBusiness>;
  categories(params?: TenantCategoryListParams): Promise<TenantCategoryListResult>;
  createCategory(input: CreateTenantCategoryInput): Promise<TenantCategory>;
  updateCategory(
    categoryId: number,
    input: UpdateTenantCategoryInput,
  ): Promise<TenantCategory>;
  deleteCategory(categoryId: number): Promise<void>;
  menuItems(params?: TenantMenuItemListParams): Promise<TenantMenuItemListResult>;
  createMenuItem(input: CreateTenantMenuItemInput): Promise<TenantMenuItem>;
  updateMenuItem(
    menuItemId: number,
    input: UpdateTenantMenuItemInput,
  ): Promise<TenantMenuItem>;
  deleteMenuItem(menuItemId: number): Promise<void>;
  upsertMenuItemImage(
    menuItemId: number,
    input: UpsertTenantMenuItemImageInput,
  ): Promise<TenantMenuItemImage>;
  deleteMenuItemImage(menuItemId: number): Promise<void>;
  /** Owner-only. */
  teamMembers(): Promise<TenantTeamMember[]>;
  /**
   * Owner-only. Changes an existing admin/editor member's role; the owner's
   * own membership and any other owner row can never be targeted — the
   * backend rejects that regardless of what id is passed. A same-role
   * request succeeds idempotently. Rejects with TenantApiError(404) for a
   * missing, cross-tenant, inactive, or owner-role target.
   */
  updateTeamMemberRole(
    membershipId: number,
    input: UpdateTenantTeamMemberRoleInput,
  ): Promise<TenantTeamMember>;
  /**
   * Owner-only. Deactivates an existing admin/editor membership
   * (is_active=false only); the owner's own membership can never be
   * targeted. Resolves on 204 No Content. Rejects with
   * TenantApiError(404) for a missing, cross-tenant, or already-removed
   * target — repeating the call after a successful removal rejects the
   * same way rather than resolving again.
   */
  removeTeamMember(membershipId: number): Promise<void>;
  /** Owner-only. Unresolved (pending or expired) invitations only. */
  teamInvitations(): Promise<TenantTeamInvitation[]>;
  /**
   * Owner-only. Rejects with TenantApiError(503) when mail delivery is
   * disabled for this deployment — no invitation row is created in that
   * case — and TenantApiError(409) when a valid unresolved invitation
   * already exists for this email or the email already has an active
   * membership.
   */
  createInvitation(
    input: CreateTenantInvitationInput,
  ): Promise<TenantTeamInvitation>;
  /**
   * Owner-only. Rotates the invitation's token in place; the previous token
   * becomes invalid immediately. Resolves on 204 No Content.
   */
  resendInvitation(invitationId: number): Promise<void>;
  /** Owner-only. Resolves on 204 No Content. */
  revokeInvitation(invitationId: number): Promise<void>;
  /**
   * Public, unauthenticated. Rejects with TenantApiError(422) for any
   * unusable token (unknown, expired, revoked, or already accepted) — the
   * backend deliberately never distinguishes those reasons.
   */
  inspectInvitation(
    input: InspectTenantInvitationInput,
  ): Promise<TenantInvitationInspect>;
  /**
   * Public, unauthenticated. Resolves on 204 No Content — no session token
   * is ever issued, existing account or new account alike. Rejects with
   * TenantApiError(422) for an unusable token, TenantApiError(409) for the
   * expected new-account race (an account was created for this email
   * between inspect and accept — the caller should re-inspect and switch to
   * the existing-account flow).
   */
  acceptInvitation(input: AcceptTenantInvitationInput): Promise<void>;
  /**
   * Owner/admin/editor. Creates a preview job and normalized preview rows —
   * never a category or menu item. Rejects with TenantApiError(413) if the
   * file exceeds the backend's configured size limit.
   */
  previewMenuImportCSV(
    input: PreviewTenantMenuImportCSVInput,
  ): Promise<TenantMenuImportPreviewResponse>;
  /**
   * Owner/admin/editor. Fetches and extracts a candidate menu from an HTTPS
   * URL server-side and creates the same kind of preview job CSV does — never
   * a category or menu item. The submitted URL is never echoed back or
   * otherwise present in the response. Rejects with TenantApiError(422) for
   * an unsafe/unreachable/unsupported source, or TenantApiError(429) if the
   * per-user rate limit is exceeded.
   */
  previewMenuImportURL(
    input: PreviewTenantMenuImportURLInput,
  ): Promise<TenantMenuImportPreviewResponse>;
  /**
   * Owner/admin/editor. Extracts a candidate menu from an uploaded text-based
   * PDF server-side and creates the same kind of preview job CSV does — never
   * a category or menu item. Rejects with TenantApiError(413) if the file
   * exceeds the configured size limit, TenantApiError(422) for an encrypted,
   * malformed, over-long or text-less PDF, or TenantApiError(429) if the
   * per-user rate limit is exceeded.
   */
  previewMenuImportPDF(
    input: PreviewTenantMenuImportPDFInput,
  ): Promise<TenantMenuImportPreviewResponse>;
  /**
   * Owner/admin/editor. Confirms a previously previewed job — CSV- or
   * URL-sourced alike, through the one shared endpoint — turning the selected
   * still-valid rows into real categories/menu items. Rejects with
   * TenantApiError(409) if the job is no longer confirmable (already
   * confirmed, or its selection changed underneath it).
   */
  confirmMenuImport(
    jobId: number,
    input: ConfirmTenantMenuImportInput,
  ): Promise<TenantMenuImportConfirmResponse>;
}

export interface CreateTenantClientOptions {
  /**
   * Base URL for the API. In the dev server this is empty so requests are
   * proxied by Vite; in a deployed build set it to the API origin.
   */
  baseUrl?: string;
  /**
   * Returns the current tenant bearer token, or null when the user is not
   * signed in. Called on every protected request so the caller controls
   * storage.
   */
  getToken: () => string | null;
  /**
   * Called when a protected request comes back 401. The panel uses this to
   * clear the stored token and force the user back to /login. Not invoked
   * for the login call itself.
   */
  onUnauthorized?: () => void;
}

export function createTenantClient(
  opts: CreateTenantClientOptions,
): TenantClient {
  const base = (opts.baseUrl ?? "").replace(/\/+$/, "");

  async function authorizedRequest<T>(
    method: "GET" | "POST" | "PUT" | "PATCH",
    path: string,
    // Input is widened to `unknown` (default z.ZodType<T> pins Input =
    // Output): a schema with any .catch()/.transform() step (branding's
    // per-field and whole-object .catch() included) has an _input type
    // looser than its _output, and pinning Input = Output made T impossible
    // to infer from Output alone — TS fell back to widening the mismatched
    // field (e.g. branding) to `unknown`. This is safe, not a real
    // type-safety loosening: schema.safeParse() below is always called with
    // envelope.data, which is already typed `unknown` (raw parsed JSON) —
    // no caller here ever passed a concretely-typed value whose input shape
    // this constraint was protecting.
    schema: z.ZodType<T, z.ZodTypeDef, unknown>,
    jsonBody?: unknown,
  ): Promise<{ data: T; meta: PaginationMeta | undefined }> {
    const token = opts.getToken();
    const headers = new Headers();
    headers.set("Accept", "application/json");
    if (jsonBody !== undefined) {
      headers.set("Content-Type", "application/json");
    }
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const init: RequestInit = {
      method,
      headers,
      credentials: "omit",
    };
    if (jsonBody !== undefined) {
      init.body = JSON.stringify(jsonBody);
    }

    const res = await fetch(`${base}/api/v1${path}`, init);

    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // fall through: no JSON body
    }

    if (!res.ok) {
      if (res.status === 401 && opts.onUnauthorized) {
        opts.onUnauthorized();
      }
      const err = body as Partial<ErrorEnvelope> | null;
      throw new TenantApiError(
        res.status,
        err?.error_code ?? "http_error",
        err?.error_message ?? `Request failed with status ${res.status}`,
      );
    }

    const envelope = body as SuccessEnvelope<unknown> | null;
    if (!envelope || envelope.success !== true) {
      throw new TenantApiError(
        res.status,
        "malformed_response",
        "Response envelope was missing or not marked successful",
      );
    }

    const parsed = schema.safeParse(envelope.data);
    if (!parsed.success) {
      throw new TenantApiError(
        res.status,
        "schema_mismatch",
        "Response data did not match expected shape",
      );
    }

    let meta: PaginationMeta | undefined;
    if (envelope.meta !== undefined) {
      const metaParsed = paginationMetaSchema.safeParse(envelope.meta);
      if (!metaParsed.success) {
        throw new TenantApiError(
          res.status,
          "schema_mismatch",
          "Response meta did not match expected shape",
        );
      }
      meta = metaParsed.data;
    }

    return { data: parsed.data, meta };
  }

  // The 204 counterpart. A successful response has no body at all, so nothing
  // is parsed and no success envelope is expected; only a failure body is read,
  // and only to recover the safe public error message. jsonBody is optional
  // because every DELETE caller sends none; PUT /users/me/password and POST
  // /team/invitations/:id/resend are callers that don't.
  async function authorizedNoContentRequest(
    method: "DELETE" | "PUT" | "POST",
    path: string,
    jsonBody?: unknown,
  ): Promise<void> {
    const token = opts.getToken();
    const headers = new Headers();
    headers.set("Accept", "application/json");
    if (jsonBody !== undefined) {
      headers.set("Content-Type", "application/json");
    }
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const init: RequestInit = {
      method,
      headers,
      credentials: "omit",
    };
    if (jsonBody !== undefined) {
      init.body = JSON.stringify(jsonBody);
    }

    const res = await fetch(`${base}/api/v1${path}`, init);

    if (res.ok) {
      return;
    }

    if (res.status === 401 && opts.onUnauthorized) {
      opts.onUnauthorized();
    }

    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // fall through: no JSON body
    }

    const err = body as Partial<ErrorEnvelope> | null;
    throw new TenantApiError(
      res.status,
      err?.error_code ?? "http_error",
      err?.error_message ?? `Request failed with status ${res.status}`,
    );
  }

  // The multipart counterpart of authorizedRequest, for the one endpoint
  // that takes a file: Content-Type is deliberately never set on the
  // request so the browser generates the multipart boundary itself.
  async function authorizedMultipartRequest<T>(
    path: string,
    schema: z.ZodType<T>,
    formData: FormData,
  ): Promise<T> {
    const token = opts.getToken();
    const headers = new Headers();
    headers.set("Accept", "application/json");
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const res = await fetch(`${base}/api/v1${path}`, {
      method: "POST",
      headers,
      credentials: "omit",
      body: formData,
    });

    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // fall through: no JSON body
    }

    if (!res.ok) {
      if (res.status === 401 && opts.onUnauthorized) {
        opts.onUnauthorized();
      }
      const err = body as Partial<ErrorEnvelope> | null;
      throw new TenantApiError(
        res.status,
        err?.error_code ?? "http_error",
        err?.error_message ?? `Request failed with status ${res.status}`,
      );
    }

    const envelope = body as SuccessEnvelope<unknown> | null;
    if (!envelope || envelope.success !== true) {
      throw new TenantApiError(
        res.status,
        "malformed_response",
        "Response envelope was missing or not marked successful",
      );
    }

    const parsed = schema.safeParse(envelope.data);
    if (!parsed.success) {
      throw new TenantApiError(
        res.status,
        "schema_mismatch",
        "Response data did not match expected shape",
      );
    }

    return parsed.data;
  }

  return {
    async login(credentials) {
      const res = await fetch(`${base}/api/v1/auth/login`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "omit",
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password,
        }),
      });

      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        // fall through: no JSON body
      }

      if (!res.ok) {
        const err = body as Partial<ErrorEnvelope> | null;
        throw new TenantApiError(
          res.status,
          err?.error_code ?? "http_error",
          err?.error_message ?? `Login failed with status ${res.status}`,
        );
      }

      const envelope = body as SuccessEnvelope<unknown> | null;
      if (!envelope || envelope.success !== true) {
        throw new TenantApiError(
          res.status,
          "malformed_response",
          "Response envelope was missing or not marked successful",
        );
      }

      const parsed = tenantLoginResponseSchema.safeParse(envelope.data);
      if (!parsed.success) {
        throw new TenantApiError(
          res.status,
          "schema_mismatch",
          "Login response did not match expected shape",
        );
      }

      return parsed.data;
    },
    async register(input) {
      const res = await fetch(`${base}/api/v1/auth/register`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "omit",
        body: JSON.stringify({
          name: input.name,
          surname: input.surname,
          email: input.email,
          phone: input.phone,
          password: input.password,
          business_name: input.business_name,
          business_slug: input.business_slug,
        }),
      });

      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        // fall through: no JSON body
      }

      if (!res.ok) {
        const err = body as Partial<ErrorEnvelope> | null;
        throw new TenantApiError(
          res.status,
          err?.error_code ?? "http_error",
          err?.error_message ?? `Register failed with status ${res.status}`,
        );
      }

      const envelope = body as SuccessEnvelope<unknown> | null;
      if (!envelope || envelope.success !== true) {
        throw new TenantApiError(
          res.status,
          "malformed_response",
          "Response envelope was missing or not marked successful",
        );
      }

      const parsed = tenantRegisterResponseSchema.safeParse(envelope.data);
      if (!parsed.success) {
        throw new TenantApiError(
          res.status,
          "schema_mismatch",
          "Register response did not match expected shape",
        );
      }

      return parsed.data;
    },
    // Deliberately not authorizedRequest: forgot/reset-password are public
    // endpoints and must never attach a bearer token, even if the caller
    // happens to hold one in memory (e.g. a signed-in tab reusing the tab).
    async forgotPassword(input) {
      const res = await fetch(`${base}/api/v1/auth/forgot-password`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "omit",
        body: JSON.stringify({ email: input.email }),
      });

      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        // fall through: no JSON body
      }

      if (!res.ok) {
        const err = body as Partial<ErrorEnvelope> | null;
        throw new TenantApiError(
          res.status,
          err?.error_code ?? "http_error",
          err?.error_message ?? `Forgot password failed with status ${res.status}`,
        );
      }

      const envelope = body as SuccessEnvelope<unknown> | null;
      if (!envelope || envelope.success !== true) {
        throw new TenantApiError(
          res.status,
          "malformed_response",
          "Response envelope was missing or not marked successful",
        );
      }
      // No data schema to validate against: the backend's data is always
      // null for this fixed, enumeration-safe 202 response.
    },
    // The 204 counterpart of forgotPassword: a successful response has no
    // body, so nothing is parsed and res.json() is only ever called on a
    // failure response, to recover the safe public error message.
    async resetPassword(input) {
      const res = await fetch(`${base}/api/v1/auth/reset-password`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "omit",
        body: JSON.stringify({
          token: input.token,
          new_password: input.new_password,
        }),
      });

      if (res.ok) {
        return;
      }

      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        // fall through: no JSON body
      }

      const err = body as Partial<ErrorEnvelope> | null;
      throw new TenantApiError(
        res.status,
        err?.error_code ?? "http_error",
        err?.error_message ?? `Reset password failed with status ${res.status}`,
      );
    },
    // Deliberately not authorizedRequest/authorizedNoContentRequest:
    // verify-email is a public endpoint and must never attach a bearer
    // token, even if the caller happens to hold one in memory (e.g. a
    // signed-in tab opening its own verification link). The 204 counterpart
    // of forgotPassword's pattern: a successful response has no body, so
    // nothing is parsed and res.json() is only ever called on a failure
    // response, to recover the safe public error message.
    async verifyEmail(input) {
      const res = await fetch(`${base}/api/v1/auth/verify-email`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "omit",
        body: JSON.stringify({ token: input.token }),
      });

      if (res.ok) {
        return;
      }

      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        // fall through: no JSON body
      }

      const err = body as Partial<ErrorEnvelope> | null;
      throw new TenantApiError(
        res.status,
        err?.error_code ?? "http_error",
        err?.error_message ?? `Verify email failed with status ${res.status}`,
      );
    },
    async resendEmailVerification() {
      await authorizedNoContentRequest("POST", "/users/me/verification/resend");
    },
    async me() {
      const { data } = await authorizedRequest(
        "GET",
        "/users/me",
        tenantMeResponseSchema,
      );
      return data;
    },
    async changePassword(input) {
      await authorizedNoContentRequest("PUT", "/users/me/password", {
        current_password: input.current_password,
        new_password: input.new_password,
      });
    },
    async businesses() {
      const { data } = await authorizedRequest(
        "GET",
        "/businesses",
        tenantBusinessMembershipListSchema,
      );
      return data;
    },
    async switchBusiness(businessId) {
      if (!Number.isInteger(businessId) || businessId <= 0) {
        throw new TenantApiError(
          400,
          "invalid_id",
          "Business id must be a positive integer",
        );
      }
      const { data } = await authorizedRequest(
        "POST",
        "/auth/switch-business",
        tenantLoginResponseSchema,
        { business_id: businessId },
      );
      return data;
    },
    async currentBusiness() {
      const { data } = await authorizedRequest(
        "GET",
        "/businesses/current",
        tenantCurrentBusinessSchema,
      );
      return data;
    },
    async setBusinessPublished(input) {
      const { data } = await authorizedRequest(
        "PATCH",
        "/businesses/current/publish",
        tenantCurrentBusinessSchema,
        { is_published: input.is_published },
      );
      return data;
    },
    async updateBusinessBranding(input) {
      // Shares the 204 no-content path used by deleteCategory/
      // removeTeamMember: no response schema is parsed on success, and
      // business_id is never part of the body — the active tenant comes
      // from the verified token, same as every other /businesses/current
      // write.
      await authorizedNoContentRequest("PUT", "/businesses/current/branding", {
        accent_preset: input.accent_preset,
        theme_mode: input.theme_mode,
        font_preset: input.font_preset,
        radius_preset: input.radius_preset,
      });
    },
    async updateBusinessProfile(input) {
      const { data } = await authorizedRequest(
        "PUT",
        "/businesses/current/profile",
        tenantCurrentBusinessSchema,
        {
          name: input.name,
          address: input.address,
          phone: input.phone,
          logo_url: input.logo_url,
          location_url: input.location_url,
        },
      );
      return data;
    },
    async categories(params) {
      const search = new URLSearchParams();
      if (params?.includeInactive) {
        search.set("include_inactive", "true");
      }
      if (params?.limit != null) {
        search.set("limit", String(params.limit));
      }
      if (params?.offset != null) {
        search.set("offset", String(params.offset));
      }
      const qs = search.toString();
      const path = qs ? `/categories?${qs}` : "/categories";
      const { data, meta } = await authorizedRequest(
        "GET",
        path,
        tenantCategoryListSchema,
      );
      return {
        items: data,
        meta: meta ?? { total: data.length, limit: data.length, offset: 0 },
      };
    },
    async createCategory(input) {
      const { data } = await authorizedRequest(
        "POST",
        "/categories",
        tenantCategorySchema,
        {
          name: input.name,
          description: input.description,
          sort_order: input.sort_order,
          is_active: input.is_active,
        },
      );
      return data;
    },
    async updateCategory(categoryId, input) {
      if (!Number.isInteger(categoryId) || categoryId <= 0) {
        throw new TenantApiError(
          400,
          "invalid_id",
          "Category id must be a positive integer",
        );
      }
      const { data } = await authorizedRequest(
        "PUT",
        `/categories/${categoryId}`,
        tenantCategorySchema,
        {
          name: input.name,
          description: input.description,
          sort_order: input.sort_order,
          is_active: input.is_active,
        },
      );
      return data;
    },
    async deleteCategory(categoryId) {
      if (!Number.isInteger(categoryId) || categoryId <= 0) {
        throw new TenantApiError(
          400,
          "invalid_id",
          "Category id must be a positive integer",
        );
      }
      await authorizedNoContentRequest("DELETE", `/categories/${categoryId}`);
    },
    async menuItems(params) {
      const search = new URLSearchParams();
      if (params?.categoryId != null) {
        search.set("category_id", String(params.categoryId));
      }
      if (params?.isActive != null) {
        search.set("is_active", String(params.isActive));
      }
      if (params?.search) {
        search.set("search", params.search);
      }
      if (params?.limit != null) {
        search.set("limit", String(params.limit));
      }
      if (params?.offset != null) {
        search.set("offset", String(params.offset));
      }
      const qs = search.toString();
      const path = qs ? `/menu-items?${qs}` : "/menu-items";
      const { data, meta } = await authorizedRequest(
        "GET",
        path,
        tenantMenuItemListSchema,
      );
      return {
        items: data,
        meta: meta ?? { total: data.length, limit: data.length, offset: 0 },
      };
    },
    async createMenuItem(input) {
      const { data } = await authorizedRequest(
        "POST",
        "/menu-items",
        tenantMenuItemSchema,
        {
          category_id: input.category_id,
          name: input.name,
          description: input.description,
          price: input.price,
          currency: input.currency,
          calories: input.calories,
          ingredients: input.ingredients,
          allergens: input.allergens,
          protein: input.protein,
          carbs: input.carbs,
          fat: input.fat,
          weight_grams: input.weight_grams,
          sort_order: input.sort_order,
          is_active: input.is_active,
        },
      );
      return data;
    },
    async updateMenuItem(menuItemId, input) {
      if (!Number.isInteger(menuItemId) || menuItemId <= 0) {
        throw new TenantApiError(
          400,
          "invalid_id",
          "Menu item id must be a positive integer",
        );
      }
      const { data } = await authorizedRequest(
        "PUT",
        `/menu-items/${menuItemId}`,
        tenantMenuItemSchema,
        {
          category_id: input.category_id,
          name: input.name,
          description: input.description,
          price: input.price,
          currency: input.currency,
          calories: input.calories,
          ingredients: input.ingredients,
          allergens: input.allergens,
          protein: input.protein,
          carbs: input.carbs,
          fat: input.fat,
          weight_grams: input.weight_grams,
          sort_order: input.sort_order,
          is_active: input.is_active,
        },
      );
      return data;
    },
    async deleteMenuItem(menuItemId) {
      if (!Number.isInteger(menuItemId) || menuItemId <= 0) {
        throw new TenantApiError(
          400,
          "invalid_id",
          "Menu item id must be a positive integer",
        );
      }
      // Shares the 204 no-content path used by deleteCategory: a success reads
      // no body, and only a failure body is parsed to recover the safe message.
      await authorizedNoContentRequest("DELETE", `/menu-items/${menuItemId}`);
    },
    async upsertMenuItemImage(menuItemId, input) {
      if (!Number.isInteger(menuItemId) || menuItemId <= 0) {
        throw new TenantApiError(
          400,
          "invalid_id",
          "Menu item id must be a positive integer",
        );
      }
      const { data } = await authorizedRequest(
        "PUT",
        `/menu-items/${menuItemId}/image`,
        tenantMenuItemImageSchema,
        {
          image_url: input.image_url,
          alt_text: input.alt_text,
        },
      );
      return data;
    },
    async deleteMenuItemImage(menuItemId) {
      if (!Number.isInteger(menuItemId) || menuItemId <= 0) {
        throw new TenantApiError(
          400,
          "invalid_id",
          "Menu item id must be a positive integer",
        );
      }
      // Shares the 204 no-content path used by deleteMenuItem/deleteCategory: a
      // success reads no body, and only a failure body is parsed for its safe
      // public message.
      await authorizedNoContentRequest(
        "DELETE",
        `/menu-items/${menuItemId}/image`,
      );
    },
    async teamMembers() {
      const { data } = await authorizedRequest(
        "GET",
        "/team/members",
        tenantTeamMemberListSchema,
      );
      return data;
    },
    async updateTeamMemberRole(membershipId, input) {
      if (!Number.isInteger(membershipId) || membershipId <= 0) {
        throw new TenantApiError(
          400,
          "invalid_id",
          "Membership id must be a positive integer",
        );
      }
      const { data } = await authorizedRequest(
        "PATCH",
        `/team/members/${membershipId}`,
        tenantTeamMemberSchema,
        { role: input.role },
      );
      return data;
    },
    async removeTeamMember(membershipId) {
      if (!Number.isInteger(membershipId) || membershipId <= 0) {
        throw new TenantApiError(
          400,
          "invalid_id",
          "Membership id must be a positive integer",
        );
      }
      await authorizedNoContentRequest(
        "DELETE",
        `/team/members/${membershipId}`,
      );
    },
    async teamInvitations() {
      const { data } = await authorizedRequest(
        "GET",
        "/team/invitations",
        tenantTeamInvitationListSchema,
      );
      return data;
    },
    async createInvitation(input) {
      const { data } = await authorizedRequest(
        "POST",
        "/team/invitations",
        tenantTeamInvitationSchema,
        { email: input.email, role: input.role },
      );
      return data;
    },
    async resendInvitation(invitationId) {
      if (!Number.isInteger(invitationId) || invitationId <= 0) {
        throw new TenantApiError(
          400,
          "invalid_id",
          "Invitation id must be a positive integer",
        );
      }
      await authorizedNoContentRequest(
        "POST",
        `/team/invitations/${invitationId}/resend`,
      );
    },
    async revokeInvitation(invitationId) {
      if (!Number.isInteger(invitationId) || invitationId <= 0) {
        throw new TenantApiError(
          400,
          "invalid_id",
          "Invitation id must be a positive integer",
        );
      }
      await authorizedNoContentRequest(
        "DELETE",
        `/team/invitations/${invitationId}`,
      );
    },
    // Deliberately not authorizedRequest: inspect/accept are public endpoints
    // and must never attach a bearer token, even if the caller happens to
    // hold one in memory (e.g. a signed-in tab opening its own invite link).
    async inspectInvitation(input) {
      const res = await fetch(`${base}/api/v1/auth/invitations/inspect`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "omit",
        body: JSON.stringify({ token: input.token }),
      });

      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        // fall through: no JSON body
      }

      if (!res.ok) {
        const err = body as Partial<ErrorEnvelope> | null;
        throw new TenantApiError(
          res.status,
          err?.error_code ?? "http_error",
          err?.error_message ??
            `Inspect invitation failed with status ${res.status}`,
        );
      }

      const envelope = body as SuccessEnvelope<unknown> | null;
      if (!envelope || envelope.success !== true) {
        throw new TenantApiError(
          res.status,
          "malformed_response",
          "Response envelope was missing or not marked successful",
        );
      }

      const parsed = tenantInvitationInspectSchema.safeParse(envelope.data);
      if (!parsed.success) {
        throw new TenantApiError(
          res.status,
          "schema_mismatch",
          "Inspect invitation response did not match expected shape",
        );
      }

      return parsed.data;
    },
    // The 204 counterpart of inspectInvitation: a successful response has no
    // body, so nothing is parsed and res.json() is only ever called on a
    // failure response, to recover the safe public error message.
    async acceptInvitation(input) {
      const res = await fetch(`${base}/api/v1/auth/invitations/accept`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "omit",
        body: JSON.stringify({
          token: input.token,
          name: input.name,
          surname: input.surname,
          password: input.password,
        }),
      });

      if (res.ok) {
        return;
      }

      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        // fall through: no JSON body
      }

      const err = body as Partial<ErrorEnvelope> | null;
      throw new TenantApiError(
        res.status,
        err?.error_code ?? "http_error",
        err?.error_message ??
          `Accept invitation failed with status ${res.status}`,
      );
    },
    async previewMenuImportCSV(input) {
      const formData = new FormData();
      formData.append("file", input.file);
      formData.append("delimiter", input.delimiter);
      formData.append("decimal_separator", input.decimal_separator);
      return authorizedMultipartRequest(
        "/menu-imports/preview",
        tenantMenuImportPreviewResponseSchema,
        formData,
      );
    },
    async previewMenuImportURL(input) {
      const { data } = await authorizedRequest(
        "POST",
        "/menu-imports/url/preview",
        tenantMenuImportPreviewResponseSchema,
        { url: input.url },
      );
      return data;
    },
    async previewMenuImportPDF(input) {
      const formData = new FormData();
      formData.append("file", input.file);
      return authorizedMultipartRequest(
        "/menu-imports/pdf/preview",
        tenantMenuImportPreviewResponseSchema,
        formData,
      );
    },
    async confirmMenuImport(jobId, input) {
      if (!Number.isInteger(jobId) || jobId <= 0) {
        throw new TenantApiError(
          400,
          "invalid_id",
          "Import job id must be a positive integer",
        );
      }
      const { data } = await authorizedRequest(
        "POST",
        `/menu-imports/${jobId}/confirm`,
        tenantMenuImportConfirmResponseSchema,
        { item_ids: input.item_ids },
      );
      return data;
    },
  };
}
